import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DashboardCard from './DashboardCard';
import Button from '../../../components/ui/Button';
import { approveLeave } from '../../../services/leaveService';
import {
  ListRow, RowMain, RowName, RowSub, MoreLink,
} from '../styles';
import { STATUS } from '../constants';
import { initialsOf } from './TodayOnLeaveCard';
import RowAvatar from './RowAvatar';

const Stages = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  margin-bottom: 14px;
`;

const Stage = styled.div`
  text-align: center;
`;

const StageValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${({ $color }) => $color};
`;

const StageLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #6B7280;
  margin-top: 2px;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  border-top: 1px solid #F0F2F5;
  padding-top: 12px;
  margin-bottom: 4px;
`;

const WaitText = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${STATUS.critical};
  white-space: nowrap;
`;

const ApproveBtn = styled(Button)`
  padding: 5px 12px;
  font-size: 12px;
  border-width: 1.5px;
`;

const STAGE_COLORS = ['#2a78d6', '#eda100', '#4a3aa7', '#1baf7a', '#E53935'];

export default function ApprovalPipelineCard({ slice, onApproved }) {
  const { t } = useTranslation();
  const history = useHistory();
  const data = slice.data;
  const [approving, setApproving] = useState(null);

  const handleApprove = useCallback(async (leaveId) => {
    setApproving(leaveId);
    try {
      await approveLeave(leaveId);
      onApproved?.();
    } catch {
      // handled by axios interceptor
    } finally {
      setApproving(null);
    }
  }, [onApproved]);

  const stages = data ? [
    { key: 'new', value: data.newRequests, label: t('leaveDashboard.pipeline.stageNew') },
    { key: 'first', value: data.firstStage, label: t('leaveDashboard.pipeline.stageFirst') },
    { key: 'later', value: data.laterStages, label: t('leaveDashboard.pipeline.stageLater') },
    { key: 'approved', value: data.approvedThisMonth, label: t('leaveDashboard.pipeline.approvedThisMonth') },
    { key: 'rejected', value: data.rejectedThisMonth, label: t('leaveDashboard.pipeline.rejectedThisMonth') },
  ] : [];

  return (
    <DashboardCard
      cardKey="onay-hatti"
      title={t('leaveDashboard.pipeline.title')}
      meta={t('leaveDashboard.pipeline.meta')}
      span={4}
      spanMd={6}
      loading={slice.loading}
      empty={!data}
    >
      {data && (
        <div>
          <Stages>
            {stages.map((stage, i) => (
              <Stage key={stage.key}>
                <StageValue $color={STAGE_COLORS[i]}>{stage.value}</StageValue>
                <StageLabel>{stage.label}</StageLabel>
              </Stage>
            ))}
          </Stages>
          <SectionTitle>{t('leaveDashboard.pipeline.slaExceeded')}</SectionTitle>
          {data.slaExceeded.length === 0 ? (
            <RowSub>{t('leaveDashboard.pipeline.slaEmpty')}</RowSub>
          ) : data.slaExceeded.map((item) => (
            <ListRow key={item.leaveId}>
              <RowAvatar src={item.profilePicture} name={item.fullName} initials={initialsOf(item.fullName)} />
              <RowMain
                style={{ cursor: 'pointer' }}
                onClick={() => history.push(`/leaves/${item.leaveId}`)}
              >
                <RowName>{item.fullName}</RowName>
                {item.department && <RowSub>{item.department}</RowSub>}
              </RowMain>
              <WaitText>{t('leaveDashboard.pipeline.waitingDays', { days: item.daysWaiting })}</WaitText>
              <ApproveBtn
                variant="outline"
                disabled={approving === item.leaveId}
                onClick={() => handleApprove(item.leaveId)}
              >
                {t('leaveDashboard.pipeline.approve')}
              </ApproveBtn>
            </ListRow>
          ))}
          <MoreLink type="button" onClick={() => history.push('/all-leaves')}>
            {t('leaveDashboard.pipeline.allLeavesLink')}
          </MoreLink>
        </div>
      )}
    </DashboardCard>
  );
}
